// ================================
// STAFF DASHBOARD
// A deliberately narrow screen — the one thing this role exists to do
// (sign up new members), plus their own earnings/payout and a line to
// admin. No dispatch, no menu, no other members' data.
//
// Tabbed the same way driver's Queue.js and the customer pages are
// (.app-shell + bottom .nav-bar) instead of one long scroll — Sign Up /
// Activity / Earnings, same split driver uses for Queue / History /
// Earnings.
// ================================
import React, { useEffect, useState, useRef } from 'react';
import { useAuth } from '../../context/AuthContext';
import { useSocket } from '../../context/SocketContext';
import axios from 'axios';
import { Html5Qrcode } from 'html5-qrcode';
import MemberSignupForm from '../../components/shared/MemberSignupForm';
import ChatThread from '../../components/shared/ChatThread';

export default function StaffDashboard() {
  const { user, logout } = useAuth();
  const socket = useSocket();
  const [tab, setTab] = useState('signup');
  const [earnings, setEarnings] = useState({ balance: 0, today: 0, week: 0 });
  const [history, setHistory] = useState([]); // itemized earnings log — one row per lead signed up / lead converted
  const [adding, setAdding] = useState(false);
  const [lastResult, setLastResult] = useState(null); // { mid, earned, conversionRate }
  const [formKey, setFormKey] = useState(0); // bumped on a successful add to remount the form with blank fields
  const [cashingOut, setCashingOut] = useState(false);
  const [showChat, setShowChat] = useState(false);
  const [unread, setUnread] = useState(0);
  // The physical card about to be handed to whoever this staffer is signing
  // up — admin pre-generates a batch and hands the stack over, so this is
  // "which one of my cards am I giving this specific person" rather than
  // the system just grabbing whatever's next in the shared pool (which
  // could easily not match what's physically in the staffer's hand).
  const [cardMid, setCardMid] = useState('');
  // True only when the card came from "I don't have any cards with me" —
  // every other path (scan, manual entry) means a physical card is
  // actually in hand right now, so admin doesn't need to do anything.
  const [noCardInHand, setNoCardInHand] = useState(false);
  const [showCardScanner, setShowCardScanner] = useState(false);
  const [findingCard, setFindingCard] = useState(false);
  const cardScannerRef = useRef(null);
  const [signups, setSignups] = useState([]); // everyone this staffer has ever signed up — for routine check-ins
  const [checkinTarget, setCheckinTarget] = useState(null); // signup whose check-in thread is open
  const [promos, setPromos] = useState([]); // incentive bulletins admin posts for staff to read
  const [promoProgress, setPromoProgress] = useState([]); // this staffer's own progress toward any live signup-goal promo

  useEffect(() => {
    if (!showCardScanner) return;
    const scanner = new Html5Qrcode('staff-card-qr-reader');
    cardScannerRef.current = scanner;
    let stopped = false;
    let started = false;

    scanner.start(
      { facingMode: 'environment' },
      { fps: 10, qrbox: 220 },
      (decodedText) => {
        if (stopped) return;
        stopped = true;
        // Same card URL shape as everywhere else (https://.../c/<mid>).
        const match = /\/c\/([^/?#]+)/.exec(decodedText.trim());
        setCardMid(match ? match[1] : decodedText.trim());
        setNoCardInHand(false); // scanning a real card means one's in hand
        setShowCardScanner(false);
      },
      () => {}
    ).then(() => { started = true; })
     .catch(() => alert('Could not access the camera. Check camera permissions and try again.'));

    return () => {
      stopped = true;
      if (!started) return;
      try { scanner.stop().then(() => scanner.clear()).catch(() => {}); } catch (e) {}
    };
  }, [showCardScanner]);

  useEffect(() => {
    fetchEarnings();
    fetchHistory();
    fetchUnread();
    fetchSignups();
    fetchPromos();
    fetchPromoProgress();
    // Fallback polling in case a socket push is missed (phone backgrounded, brief disconnect, etc.)
    const poll = setInterval(() => { fetchEarnings(); fetchUnread(); }, 15000);
    return () => clearInterval(poll);
  }, []);

  useEffect(() => {
    if (!socket) return;
    const onReconnect = () => { fetchEarnings(); fetchHistory(); fetchUnread(); };
    socket.on('cashout:approved', () => { fetchEarnings(); alert('✅ Your payout was approved.'); });
    socket.on('earnings:updated', ({ earned, promoTitle }) => {
      fetchEarnings(); fetchHistory(); fetchPromoProgress();
      alert(promoTitle ? `🎉 Promo goal hit — "${promoTitle}" — +${earned} coins credited!` : `🎉 A lead converted — +${earned} coins credited to your balance.`);
    });
    socket.on('message:new', fetchUnread);
    socket.on('promos:updated', () => { fetchPromos(); fetchPromoProgress(); });
    socket.on('connect', onReconnect); // catch up on reconnect
    return () => { socket.off('cashout:approved'); socket.off('earnings:updated'); socket.off('message:new', fetchUnread); socket.off('promos:updated'); socket.off('connect', onReconnect); };
  }, [socket]);

  async function fetchUnread() {
    try {
      const r = await axios.get('/api/messages/my-unread');
      setUnread(r.data.totalUnread);
    } catch (e) {}
  }

  // Opening the chat marks it read server-side — refetch once closed so
  // the badge clears instead of sitting on a stale count.
  function closeChat() {
    setShowChat(false);
    fetchUnread();
  }

  async function fetchHistory() {
    try {
      const r = await axios.get('/api/staff/earnings/history');
      setHistory(r.data.entries || []);
    } catch (e) {}
  }

  async function fetchEarnings() {
    try {
      const r = await axios.get('/api/staff/earnings');
      setEarnings(r.data);
    } catch (e) {}
  }

  async function fetchSignups() {
    try {
      const r = await axios.get('/api/staff/my-signups');
      setSignups(r.data.members || []);
    } catch (e) {}
  }

  async function fetchPromos() {
    try {
      const r = await axios.get('/api/promos/live');
      setPromos(r.data.promos || []);
    } catch (e) {}
  }

  async function fetchPromoProgress() {
    try {
      const r = await axios.get('/api/promos/my-progress');
      setPromoProgress(r.data.progress || []);
    } catch (e) {}
  }

  // For when this staffer has no physical cards on them at all — the
  // signup still needs a card attached right now, so this grabs the next
  // unclaimed M.I.D. from the shared pool and flags it as something admin
  // needs to print and get to this person separately.
  async function findNextCard() {
    setFindingCard(true);
    try {
      const r = await axios.get('/api/staff/next-card');
      setCardMid(r.data.mid);
      setNoCardInHand(true); // this is the "I have no cards on me" path — admin has to print & deliver this one
    } catch (e) {
      alert(e.response?.data?.error || 'Failed to find a card');
    } finally {
      setFindingCard(false);
    }
  }

  async function handleAddMember(payload) {
    if (!cardMid) { alert("Scan or enter the card you're handing this person first"); return; }
    setAdding(true);
    setLastResult(null);
    try {
      const r = await axios.post('/api/staff/members', {
        memberName: payload.name,
        phone: payload.phone,
        address: payload.address,
        zip: payload.zip,
        dob: payload.dob,
        verificationMethod: payload.verificationMethod,
        cardMid,
        noCardInHand
      });
      setLastResult({ mid: r.data.mid, earned: r.data.earned, conversionRate: r.data.conversionRate, noCardInHand });
      setFormKey(k => k + 1); // fresh, blank MemberSignupForm for the next scan
      setCardMid(''); // next person needs their own card scanned/entered
      setNoCardInHand(false);
      fetchEarnings();
      fetchHistory();
      fetchSignups();
      fetchPromoProgress();
    } catch (e) {
      alert(e.response?.data?.error || 'Failed to add member');
    } finally {
      setAdding(false);
    }
  }

  async function handleCashout() {
    setCashingOut(true);
    // Self-heal if the request never settles (app backgrounded mid-request, dropped
    // connection, etc.) so the button can't stay disabled forever.
    const safety = setTimeout(() => setCashingOut(false), 20000);
    try {
      await axios.post('/api/staff/cashout-request');
      alert('Payout requested — your manager will approve it shortly.');
    } catch (e) {
      alert(e.response?.data?.error || 'Failed to request payout');
    } finally {
      clearTimeout(safety);
      setCashingOut(false);
    }
  }

  return (
    <div className="app-shell">
      {/* Header */}
      <div style={{ padding: '18px 20px 14px', borderBottom: '1px solid var(--border)', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <div>
          <p style={{ fontSize: 11, color: 'var(--text-secondary)', textTransform: 'uppercase', letterSpacing: 1 }}>Weedo World — Staff</p>
          <h2 style={{ fontFamily: 'var(--font-display)', fontSize: 20, letterSpacing: 2 }}>{user?.name}</h2>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <button onClick={() => setShowChat(true)} title="Message admin"
            style={{ position: 'relative', background: '#0a1530', color: 'var(--accent-blue)', border: '1px solid var(--border-hover)', borderRadius: 20, fontSize: 14, padding: '6px 10px', cursor: 'pointer' }}>
            💬
            {unread > 0 && (
              <span style={{ position: 'absolute', top: -5, right: -5, background: 'var(--accent-red)', color: '#fff', borderRadius: '50%', width: 16, height: 16, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 9, fontFamily: 'var(--font-body)' }}>
                {unread}
              </span>
            )}
          </button>
          <button onClick={logout} style={{ background: 'none', border: '1px solid var(--border)', color: 'var(--text-secondary)', borderRadius: 8, padding: '7px 14px', fontSize: 12, cursor: 'pointer', fontFamily: 'var(--font-body)' }}>
            Sign Out
          </button>
        </div>
      </div>

      {/* Content */}
      <div style={{ flex: 1, overflowY: 'auto', padding: '20px 20px 20px' }}>

        {tab === 'signup' && (
          <>
            {lastResult && (
              <div style={{ background: '#0a2010', border: '1px solid #0a3020', borderRadius: 14, padding: 16, marginBottom: 20 }}>
                <p style={{ fontSize: 13, color: 'var(--accent-green)' }}>
                  ✅ Member added — M.I.D. {lastResult.mid}.{' '}
                  {lastResult.noCardInHand
                    ? "Admin's been notified to print this card and get it to them."
                    : 'Hand them the card; they set their own PIN when they activate it.'}
                </p>
                {lastResult.earned > 0 && <p style={{ fontSize: 12, color: 'var(--text-secondary)', marginTop: 4 }}>+{lastResult.earned} coins credited to your balance for the lead.</p>}
                {lastResult.conversionRate > 0 && <p style={{ fontSize: 12, color: 'var(--text-secondary)', marginTop: 2 }}>Another +{lastResult.conversionRate} coins land once they complete their first order.</p>}
              </div>
            )}

            {/* Assign Card — which physical card (from the stack admin handed
                you) is going to this specific person. Scanning/entering it here
                first means the member record always matches the actual card
                they walk away with, instead of whatever the system would've
                grabbed next from the shared pool. "No cards with me" is the one
                case where there's no physical card to match at all — admin gets
                notified to print and deliver that M.I.D. separately. */}
            <div style={{ background: 'var(--bg-card)', border: '1px solid var(--border)', borderRadius: 16, padding: 20, marginBottom: 20 }}>
              <p style={{ fontFamily: 'var(--font-display)', fontSize: 18, letterSpacing: 1, marginBottom: 4 }}>Assign Card</p>
              <p style={{ fontSize: 12, color: 'var(--text-secondary)', marginBottom: 16 }}>Scan or enter the card you're handing this person — or let admin print one if you don't have any with you.</p>

              {cardMid ? (
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', background: noCardInHand ? '#1a1200' : '#0a2010', border: noCardInHand ? '1px solid #4a3000' : '1px solid #0a3020', borderRadius: 10, padding: '10px 14px' }}>
                  <span style={{ fontSize: 14, color: noCardInHand ? 'var(--accent-gold)' : 'var(--accent-green)' }}>
                    {noCardInHand ? `📦 No card in hand — M.I.D. ${cardMid} to be printed & delivered by admin` : `✅ Card ready — M.I.D. ${cardMid}`}
                  </span>
                  <button type="button" onClick={() => { setCardMid(''); setNoCardInHand(false); }} style={{ background: 'none', border: 'none', color: 'var(--text-secondary)', fontSize: 12, cursor: 'pointer', textDecoration: 'underline' }}>Change</button>
                </div>
              ) : (
                <>
                  <button type="button" onClick={() => setShowCardScanner(true)} style={{
                    width: '100%', padding: '12px', marginBottom: 12, borderRadius: 10,
                    border: '1px solid var(--border-hover)', background: '#0a1530', color: 'var(--accent-blue)', fontSize: 14, cursor: 'pointer', fontFamily: 'var(--font-body)'
                  }}>
                    📷 Scan card QR code
                  </button>

                  {showCardScanner && (
                    <div style={{ marginBottom: 12 }}>
                      <div id="staff-card-qr-reader" style={{ borderRadius: 10, overflow: 'hidden' }} />
                      <button type="button" onClick={() => setShowCardScanner(false)} style={{ marginTop: 8, background: 'none', border: 'none', color: 'var(--text-secondary)', fontSize: 12, cursor: 'pointer' }}>Cancel scan</button>
                    </div>
                  )}

                  <input type="text" placeholder="Or type the M.I.D. printed on the card" maxLength={5}
                    onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); setCardMid(e.target.value.trim()); setNoCardInHand(false); } }}
                    onBlur={e => { if (e.target.value.trim()) { setCardMid(e.target.value.trim()); setNoCardInHand(false); } }}
                    style={{ width: '100%', padding: '10px 12px', marginBottom: 12, background: 'var(--bg-input)', border: '1px solid var(--border-mid)', borderRadius: 8, color: 'var(--text-primary)', fontSize: 14, outline: 'none', fontFamily: 'var(--font-body)' }} />

                  <p style={{ fontSize: 11, color: 'var(--text-secondary)', textAlign: 'center', margin: '0 0 10px' }}>— don't have a card with you? —</p>
                  <button type="button" onClick={findNextCard} disabled={findingCard} style={{
                    width: '100%', padding: '12px', borderRadius: 10,
                    border: '1px solid #4a3000', background: '#1a1200', color: 'var(--accent-gold)', fontSize: 14, cursor: 'pointer', fontFamily: 'var(--font-body)',
                    opacity: findingCard ? 0.6 : 1
                  }}>
                    {findingCard ? 'Assigning a card...' : '📦 Sign them up anyway — admin will print & deliver a card'}
                  </button>
                </>
              )}
            </div>

            {/* Add Member */}
            <div style={{ background: 'var(--bg-card)', border: '1px solid var(--border)', borderRadius: 16, padding: 20, opacity: cardMid ? 1 : 0.5 }}>
              <p style={{ fontFamily: 'var(--font-display)', fontSize: 18, letterSpacing: 1, marginBottom: 4 }}>Add New Member</p>
              {!cardMid && <p style={{ fontSize: 12, color: 'var(--text-secondary)', marginBottom: 12 }}>Assign a card above first.</p>}
              <MemberSignupForm key={formKey} onSubmit={handleAddMember} submitting={adding} submitLabel="Add Member" />
            </div>
          </>
        )}

        {tab === 'activity' && (
          <>
            {/* My Signups — routine check-ins with the people this staffer
                actually signed up, not a general support line. */}
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', paddingBottom: 10 }}>
              <p style={{ fontFamily: 'var(--font-display)', fontSize: 16, letterSpacing: 1 }}>My Signups</p>
              <p style={{ fontSize: 11, color: 'var(--text-secondary)' }}>{signups.length} total</p>
            </div>
            {signups.length === 0 ? (
              <p style={{ fontSize: 13, color: 'var(--text-secondary)', marginBottom: 20 }}>No one yet — sign someone up to see them here.</p>
            ) : (
              <div style={{ marginBottom: 24 }}>
                {signups.map(m => (
                  <div key={m.mid} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '10px 0', borderBottom: '1px solid var(--bg-card)' }}>
                    <div>
                      <p style={{ fontSize: 13, color: 'var(--text-primary)' }}>{m.name}</p>
                      <p style={{ fontSize: 11, color: 'var(--text-secondary)', marginTop: 2 }}>M.I.D. {m.mid} · {m.status}</p>
                    </div>
                    <button onClick={() => setCheckinTarget(m)} style={{ background: '#0a1530', color: 'var(--accent-blue)', border: '1px solid var(--border-hover)', borderRadius: 20, fontSize: 12, padding: '6px 12px', cursor: 'pointer', fontFamily: 'var(--font-body)' }}>
                      💬 Check In
                    </button>
                  </div>
                ))}
              </div>
            )}

            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', paddingBottom: 10 }}>
              <p style={{ fontFamily: 'var(--font-display)', fontSize: 16, letterSpacing: 1 }}>Recent Earnings</p>
              <p style={{ fontSize: 11, color: 'var(--text-secondary)' }}>{history.length} total</p>
            </div>
            {history.length === 0 ? (
              <p style={{ fontSize: 13, color: 'var(--text-secondary)' }}>No earnings yet — sign someone up to get started.</p>
            ) : (
              history.slice(0, 30).map(h => (
                <div key={h.id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '12px 0', borderBottom: '1px solid var(--bg-card)' }}>
                  <div>
                    <p style={{ fontSize: 13, color: 'var(--text-primary)' }}>{h.memberName || 'Member'}</p>
                    <p style={{ fontSize: 11, color: 'var(--text-secondary)', marginTop: 2 }}>
                      {h.type === 'conversion' ? 'First order completed' : h.type === 'promo' ? 'Promo goal hit' : 'Signed up'} · {h.timestamp}
                    </p>
                  </div>
                  <p style={{ fontFamily: 'var(--font-display)', fontSize: 16, color: 'var(--accent-green)' }}>+{h.coins.toFixed(0)}</p>
                </div>
              ))
            )}
          </>
        )}

        {tab === 'promos' && (
          <>
            <p style={{ fontFamily: 'var(--font-display)', fontSize: 16, letterSpacing: 1, paddingBottom: 10 }}>Promos</p>
            {promos.length === 0 ? (
              <p style={{ fontSize: 13, color: 'var(--text-secondary)' }}>Nothing posted right now — check back later.</p>
            ) : (
              promos.map(p => {
                const goal = p.goalType === 'staff_signups' ? promoProgress.find(pr => pr.promo.id === p.id) : null;
                const pct = goal ? Math.min(100, Math.round((goal.count / p.goalTarget) * 100)) : 0;
                return (
                <div key={p.id} style={{ background: 'var(--bg-card)', border: '1px solid var(--border)', borderRadius: 14, padding: 16, marginBottom: 12 }}>
                  <p style={{ fontFamily: 'var(--font-display)', fontSize: 16, letterSpacing: 1, color: 'var(--accent-gold)', marginBottom: 6 }}>{p.title}</p>
                  {p.description && <p style={{ fontSize: 13, color: 'var(--text-primary)', marginBottom: 8, lineHeight: 1.5 }}>{p.description}</p>}
                  {goal && (
                    <div style={{ marginTop: 8, marginBottom: 8 }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}>
                        <span style={{ fontSize: 11, color: 'var(--text-secondary)' }}>{goal.count} of {p.goalTarget} signups</span>
                        <span style={{ fontSize: 11, color: 'var(--accent-green)' }}>+{p.goalBonusCoins}c on completion</span>
                      </div>
                      <div style={{ background: 'var(--bg-input)', borderRadius: 20, height: 8, overflow: 'hidden' }}>
                        <div style={{ width: `${pct}%`, height: '100%', background: pct >= 100 ? 'var(--accent-green)' : 'var(--accent-blue)', borderRadius: 20 }} />
                      </div>
                    </div>
                  )}
                  {p.endDate && <p style={{ fontSize: 11, color: 'var(--text-secondary)' }}>Through {p.endDate}</p>}
                </div>
                );
              })
            )}
          </>
        )}

        {tab === 'earnings' && (
          <>
            <p style={{ fontFamily: 'var(--font-display)', fontSize: 16, letterSpacing: 1, paddingBottom: 10 }}>My Earnings</p>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 20 }}>
              {[
                { label: 'Today', val: `${earnings.today.toFixed(0)} coins`, color: 'var(--accent-blue)' },
                { label: 'This Week', val: `${earnings.week.toFixed(0)} coins`, color: 'var(--accent-blue)' },
                { label: 'Balance', val: `${earnings.balance.toFixed(0)} coins`, color: 'var(--accent-green)' },
              ].map(s => (
                <div key={s.label} style={{ background: 'var(--bg-card)', border: '1px solid var(--border)', borderRadius: 12, padding: 14 }}>
                  <p style={{ fontSize: 10, color: 'var(--text-secondary)', textTransform: 'uppercase', letterSpacing: 1, marginBottom: 4 }}>{s.label}</p>
                  <p style={{ fontFamily: 'var(--font-display)', fontSize: 22, color: s.color, letterSpacing: 1 }}>{s.val}</p>
                </div>
              ))}
            </div>
            <button onClick={handleCashout} disabled={cashingOut || earnings.balance <= 0}
              style={{ width: '100%', background: '#0a2010', color: 'var(--accent-green)', border: '1px solid #0a3020', borderRadius: 12, padding: '14px', fontSize: 14, cursor: 'pointer', fontFamily: 'var(--font-body)', opacity: (cashingOut || earnings.balance <= 0) ? 0.5 : 1 }}>
              {cashingOut ? 'Requesting...' : `Request Payout (${earnings.balance.toFixed(0)} coins)`}
            </button>
          </>
        )}
      </div>

      {/* Nav */}
      <div className="nav-bar">
        <button className={`nav-item ${tab === 'signup' ? 'active' : ''}`} onClick={() => setTab('signup')}><span style={{ fontSize: 17 }}>📝</span>Sign Up</button>
        <button className={`nav-item ${tab === 'activity' ? 'active' : ''}`} onClick={() => setTab('activity')}><span style={{ fontSize: 17 }}>📋</span>Activity</button>
        <button className={`nav-item ${tab === 'promos' ? 'active' : ''}`} onClick={() => setTab('promos')}><span style={{ fontSize: 17 }}>📢</span>Promos</button>
        <button className={`nav-item ${tab === 'earnings' ? 'active' : ''}`} onClick={() => setTab('earnings')}><span style={{ fontSize: 17 }}>💰</span>Earnings</button>
      </div>

      {showChat && (
        <ChatThread
          title="Message Admin"
          selfRole="staff"
          fetchUrl={`/api/messages/thread/staff/${encodeURIComponent(user?.username)}`}
          postUrl={`/api/messages/thread/staff/${encodeURIComponent(user?.username)}`}
          onClose={closeChat}
        />
      )}

      {checkinTarget && (
        <ChatThread
          title={`Message ${checkinTarget.name}`}
          selfRole="staff"
          fetchUrl={`/api/messages/staff-checkin/${checkinTarget.mid}`}
          postUrl={`/api/messages/staff-checkin/${checkinTarget.mid}`}
          onClose={() => setCheckinTarget(null)}
        />
      )}
    </div>
  );
}
